// Content length configuration with token estimates
const lengthConfig = {
  short: {
    targetWords: 1500,
    maxTokens: 8000,   // For detailed content
    minTokens: 5000     
  },
  medium: {
    targetWords: 2500,
    maxTokens: 12000,  // For comprehensive content
    minTokens: 8000     
  },
  long: {
    targetWords: 4000,
    maxTokens: 16000,  // Increased for extensive content
    minTokens: 12000     
  }
};

import OpenAI from 'openai'
import { supabase } from './supabase.js'

const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true
})

// Move sectionLabels to the top of the file, before any function definitions
const sectionLabels = {
  keyTakeaways: {
    en: 'Key Takeaways',
    fr: 'Points Clés',
    es: 'Puntos Clave',
    de: 'Kernpunkte',
    it: 'Punti Chiave',
    pt: 'Pontos-Chave',
    nl: 'Belangrijkste Punten',
    pl: 'Kluczowe Wnioski',
    ru: 'Ключевые Моменты',
    ja: '重要なポイント',
    ko: '주요 포인트',
    zh: '重点总结',
    ar: 'النقاط الرئيسية',
    hi: 'मुख्य बिंदु',
    tr: 'Önemli Noktalar',
    vi: 'Điểm Chính'
  },
  faq: {
    en: 'Frequently Asked Questions',
    fr: 'Questions Fréquemment Posées',
    es: 'Preguntas Frecuentes',
    de: 'Häufig Gestellte Fragen',
    it: 'Domande Frequenti',
    pt: 'Perguntas Frequentes',
    nl: 'Veelgestelde Vragen',
    pl: 'Najczęściej Zadawane Pytania',
    ru: 'Часто Задаваемые Вопросы',
    ja: 'よくある質問',
    ko: '자주 묻는 질문',
    zh: '常见问题',
    ar: 'الأسئلة الشائعة',
    hi: 'अक्सर पूछे जाने वाले प्रश्न',
    tr: 'Sıkça Sorulan Sorular',
    vi: 'Câu Hỏi Thường Gặp'
  }
};

// Function to update year references to current year
function updateYearReferences(text) {
  const currentYear = new Date().getFullYear()
  // Match years between 2000 and 2099
  return text.replace(/\b20\d{2}\b/g, currentYear.toString())
}

const languageInstructions = {
  en: { instruction: "Write in English", style: "Use clear, professional English" },
  fr: { instruction: "Écrivez en français", style: "Utilisez un français professionnel" },
  es: { instruction: "Escribe en español", style: "Usa español profesional" },
  de: { instruction: "Schreiben Sie auf Deutsch", style: "Verwenden Sie professionelles Deutsch" },
  it: { instruction: "Scrivi in italiano", style: "Usa un italiano professionale" },
  pt: { instruction: "Escreva em português", style: "Use português profissional" },
  nl: { instruction: "Schrijf in het Nederlands", style: "Gebruik professioneel Nederlands" },
  pl: { instruction: "Pisz po polsku", style: "Używaj profesjonalnego języka polskiego" },
  ru: { instruction: "Пишите на русском", style: "Используйте профессиональный русский язык" },
  ja: { instruction: "日本語で書いてください", style: "プロフェッショナルな日本語を使用" },
  ko: { instruction: "한국어로 작성하세요", style: "전문적인 한국어 사용" },
  zh: { instruction: "用中文写作", style: "使用专业的中文" },
  ar: { instruction: "اكتب بالعربية", style: "استخدم لغة عربية احترافية" },
  hi: { instruction: "हिंदी में लिखें", style: "पेशेवर हिंदी का प्रयोग करें" },
  tr: { instruction: "Türkçe yazın", style: "Profesyonel Türkçe kullanın" },
  vi: { instruction: "Viết bằng tiếng Việt", style: "Sử dụng tiếng Việt chuyên nghiệp" }
}

export async function generateBlogIdeas(keyword, searchVolume, difficulty, contentType, searchIntent, language_code = 'en') {
  try {
    if (process.env.NODE_ENV === 'development') {
      console.log('Generating ideas for:', {
        keyword,
        searchVolume,
        difficulty,
        contentType,
        searchIntent,
        language_code
      })
    }
    
    // Get the content length configuration for long content
    const config = lengthConfig['long']  // Always use long content for blog ideas
    
    const contentTypePrompts = {
      'how-to': {
        en: 'Create step-by-step guides that teach readers how to accomplish specific tasks',
        fr: 'Créez des guides étape par étape qui enseignent aux lecteurs comment accomplir des tâches spécifiques',
        es: 'Crea guías paso a paso que enseñen a los lectores cómo realizar tareas específicas',
        de: 'Erstellen Sie Schritt-für-Schritt-Anleitungen, die den Lesern beibringen, wie sie bestimmte Aufgaben erledigen können'
      },
      'listicle': {
        en: 'Create list-based articles that provide multiple solutions, examples, or tips',
        fr: 'Créez des articles sous forme de liste qui fournissent plusieurs solutions, exemples ou conseils',
        es: 'Crea artículos basados en listas que proporcionen múltiples soluciones, ejemplos o consejos',
        de: 'Erstellen Sie listenbasierte Artikel, die mehrere Lösungen, Beispiele oder Tipps bieten'
      },
      // Add other content types with translations...
    }

    const searchIntentPrompts = {
      informational: {
        en: 'Focus on educating and informing the reader with comprehensive information',
        fr: 'Concentrez-vous sur l\'éducation et l\'information du lecteur avec des informations complètes',
        es: 'Enfócate en educar e informar al lector con información completa',
        de: 'Konzentrieren Sie sich darauf, den Leser mit umfassenden Informationen zu bilden und zu informieren'
      },
      commercial: {
        en: 'Include product recommendations and purchasing guidance',
        fr: 'Incluez des recommandations de produits et des conseils d\'achat',
        es: 'Incluye recomendaciones de productos y guía de compra',
        de: 'Fügen Sie Produktempfehlungen und Kaufberatung hinzu'
      },
      // Add other intents with translations...
    }

    const contentTypeGuidance = contentType ? `Content Type: ${contentType}
${contentTypePrompts[contentType]?.[language_code] || contentTypePrompts[contentType]?.['en'] || 'Create engaging and informative content'}` : ''

    const searchIntentGuidance = searchIntent ? `
Search Intent: ${searchIntent}
${searchIntentPrompts[searchIntent]?.[language_code] || searchIntentPrompts[searchIntent]?.['en']}` : ''

    const systemPrompt = `You are a professional content writer who specializes in writing in ${language_code.toUpperCase()}. 
${languageInstructions[language_code]?.instruction || "Write in the specified language"}
${languageInstructions[language_code]?.style || "Use appropriate style and grammar for the language"}

CRITICAL FORMATTING AND STYLE REQUIREMENTS:
1. The article MUST start with the EXACT title format I provide - DO NOT modify or change the title
2. The title format will be provided in the user message - use it exactly as given
3. Section headers must be formatted as: ## **Section Title**\n\n
4. Subsection headers must be formatted as: ### **Subsection Title**\n\n
5. All headers must be followed by exactly two newlines
6. Content must be properly formatted in Markdown
7. Write in a clear, professional style - avoid flowery or poetic language
8. Keep sentences concise and focused - avoid run-on sentences
9. Each paragraph should have a clear main point
10. Use proper punctuation and paragraph breaks

CRITICAL CONTENT STRUCTURE:
1. Introduction (500-600 words):
   - Clear thesis statement
   - Overview of main points
   - Context and background
   - NO philosophical rambling or metaphors

2. Key Takeaways (if requested):
   - Place immediately after introduction
   - Numbered list format
   - Each takeaway should be clear and actionable
   - Must match the requested number of takeaways

3. Main Body Sections (600-800 words each):
   - Each section must have a clear focus
   - Use concrete examples and data
   - Include relevant statistics when possible
   - Stay on topic - avoid tangents
   - Use proper subsections when needed

4. FAQ Section (if requested):
   - Place before conclusion
   - Clear Q&A format
   - Must match the requested number of FAQs
   - Each answer should be thorough and helpful

5. Conclusion (400-500 words):
   - Summarize key points
   - Provide actionable insights
   - Keep it focused and practical

STRICT CONTENT RULES:
1. Stay factual and objective
2. Avoid repetition
3. No philosophical or poetic tangents
4. Keep paragraphs focused and concise
5. Use clear topic sentences
6. Support claims with evidence
7. Maintain professional tone throughout
8. No run-on sentences or stream of consciousness writing

LENGTH REQUIREMENTS:
- Total content: Minimum ${config.targetWords} words
- Each main section: 600-800 words
- Introduction: 500-600 words
- Conclusion: 400-500 words
- Use detailed examples and evidence to meet length requirements, not filler text`;

    const userPrompt = `Generate 5 unique and engaging blog post ideas for the keyword "${keyword}".
Consider that this keyword has:
- Monthly search volume: ${searchVolume}
- SEO difficulty score: ${difficulty}

${contentTypeGuidance}
${searchIntentGuidance}

Important Guidelines:
1. Write EVERYTHING in ${language_code.toUpperCase()} language ONLY - no English translations
2. For list-based articles, the number in the title MUST match the actual number of points
3. Each idea should be practical and actionable
4. The description should give a clear preview
5. Key points should outline the main sections
6. Focus on providing valuable information to readers

Return the ideas in this exact JSON format:
{
  "ideas": [
    {
      "title": "Title (in ${language_code.toUpperCase()} only)",
      "description": "Description (in ${language_code.toUpperCase()} only)",
      "keyPoints": [
        "Key point 1 (in ${language_code.toUpperCase()} only)",
        "Key point 2 (in ${language_code.toUpperCase()} only)",
        "Key point 3 (in ${language_code.toUpperCase()} only)"
      ],
      "contentType": "${contentType || 'general'}",
      "searchIntent": "${searchIntent?.primary || searchIntent || 'informational'}",
      "language": "${language_code}"
    }
  ]
}`

    const completion = await openai.chat.completions.create({
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPrompt }
      ],
      model: "gpt-4o-mini",
      temperature: 0.7,
      max_tokens: Math.min(lengthConfig.maxTokens * 2, 4000),
      presence_penalty: 0.6,
      frequency_penalty: 0.3,
      response_format: { type: "json_object" }
    })

    const response = completion.choices[0].message.content.trim()
    if (process.env.NODE_ENV === 'development') {
    console.log('OpenAI Response:', response)
    }
    
    try {
      // Find the JSON object in the response
      const jsonMatch = response.match(/\{[\s\S]*\}/)
      if (!jsonMatch) {
        throw new Error('No JSON object found in response')
      }
      
      // Validate the JSON structure before parsing
      const jsonStr = jsonMatch[0]
      if (!jsonStr.endsWith('}')) {
        throw new Error('JSON response appears to be truncated')
      }
      
      // Parse the JSON and update year references in all text fields
      const data = JSON.parse(jsonStr)
      
      // Validate the expected structure
      if (!data.ideas || !Array.isArray(data.ideas)) {
        throw new Error('Invalid response structure: missing ideas array')
      }
      
      // Validate each idea has the required fields and is in the correct language
      data.ideas.forEach((idea, index) => {
        if (!idea.title || !idea.description || !idea.keyPoints || !Array.isArray(idea.keyPoints)) {
          throw new Error(`Invalid idea structure at index ${index}`)
        }
        // Ensure language is set correctly
        idea.language = language_code
      })
      
      // Update year references
      data.ideas = data.ideas.map(idea => ({
        ...idea,
        title: updateYearReferences(idea.title),
        description: updateYearReferences(idea.description),
        keyPoints: idea.keyPoints.map(point => updateYearReferences(point)),
        language: language_code
      }))
      
      return data.ideas || []
    } catch (parseError) {
      if (process.env.NODE_ENV === 'development') {
      console.error('Failed to parse OpenAI response:', parseError)
      console.error('Raw response:', response)
      }
      throw new Error(`Invalid response format from OpenAI: ${parseError.message}`)
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
    console.error('Error generating ideas:', error)
    }
    throw new Error('Failed to generate blog ideas: ' + error.message)
  }
}

// Helper function to count words in text
function countWords(text) {
  return text.trim().split(/\s+/).length
}

function validateAndFixOutput(content, params) {
  const { includeFaqs, numFaqs, includeTakeaways, numTakeaways, language_code, contentLength = 'medium' } = params;
  
  console.log('=== Starting Content Validation ===');
  console.log('Validation Parameters:', {
    includeFaqs,
    numFaqs,
    includeTakeaways,
    numTakeaways,
    language_code,
    contentLength,
    lengthConfig: lengthConfig[contentLength],
    contentLength: content.length,
    wordCount: countWords(content)
  });

  // Validate content length - now just logs a warning instead of throwing an error
  const wordCount = countWords(content);
  const { targetWords } = lengthConfig[contentLength];
  
  if (wordCount < targetWords * 0.5) {
    console.warn(`Content length (${wordCount} words) is significantly shorter than target length (${targetWords} words)`);
  } else if (wordCount > targetWords * 2) {
    console.warn(`Content length (${wordCount} words) is significantly longer than target length (${targetWords} words)`);
  }

  let modifiedContent = content;

  // Helper function to extract section content with improved pattern matching
  const extractSection = (variations, content) => {
    console.log('\nTrying to extract section with variations:', variations);
    
    for (const variation of variations) {
      // Try different section header patterns
      const patterns = [
        // Markdown header with content up to next header or end
        new RegExp(`(?:^|\\n)\\s*##\\s*${variation}\\s*\\n([\\s\\S]*?(?=\\n\\s*##|$))`, 'i'),
        // Standard header with newlines
        new RegExp(`(?:^|\\n)\\s*${variation}\\s*\\n([\\s\\S]*?(?=\\n\\s*##|$))`, 'i'),
        // Header with colon
        new RegExp(`(?:^|\\n)\\s*${variation}:\\s*\\n([\\s\\S]*?(?=\\n\\s*##|$))`, 'i'),
        // Header with line
        new RegExp(`(?:^|\\n)\\s*${variation}\\s*[-_=]+\\s*\\n([\\s\\S]*?(?=\\n\\s*##|$))`, 'i'),
        // Header with numbers
        new RegExp(`(?:^|\\n)\\s*\\d+\\.?\\s*${variation}\\s*\\n([\\s\\S]*?(?=\\n\\s*##|$))`, 'i')
      ];

      for (const regex of patterns) {
        console.log('Trying pattern:', regex.toString());
        const match = content.match(regex);
        if (match) {
          console.log('Found match with pattern:', regex.toString());
          console.log('Matched content preview:', match[0].substring(0, 200) + '...');
          return match[0];
        }
      }
    }
    console.log('No matches found for any patterns');
    return null;
  };

  // Helper function to count items in a section with improved pattern matching
  const countItems = (section, type) => {
    if (!section) {
      console.log(`No ${type} section provided to count`);
      return { count: 0, methods: {} };
    }

    console.log(`\nCounting items in ${type} section:`, section.substring(0, 200) + '...');
    console.log('Full section content:', section);

    const methods = type === 'faq' ? {
      // More specific FAQ patterns with improved regex
      numberedQA: (section.match(/(?:^|\n)\s*\d+\s*\.\s*Q\s*:\s*[^?]+\?\s*\n\s*A\s*:/gmi) || []).length,
      questionAnswer: (section.match(/(?:^|\n)\s*Q\s*:\s*[^?]+\?\s*\n\s*A\s*:/gmi) || []).length,
      numberedQuestions: (section.match(/(?:^|\n)\s*\d+\s*\.\s*[^?]+\?/gm) || []).length,
      simpleQuestions: (section.match(/(?:^|\n)[^?]+\?/gm) || []).length,
      // Additional patterns for FAQ-like structures
      bulletQuestions: (section.match(/(?:^|\n)\s*[-•*]\s*[^?]+\?/gm) || []).length,
      headingQuestions: (section.match(/(?:^|\n)###?\s*[^?]+\?/gm) || []).length,
      // New patterns for exact format matching
      exactFormat: (section.match(/(?:^|\n)\s*\d+\s*\.\s*Q\s*:\s*[^?]+\?\s*\n\s*A\s*:[^\n]+(?:\n|$)/gmi) || []).length,
      exactFormatWithSpacing: (section.match(/(?:^|\n)\s*\d+\s*\.\s*Q\s*:\s*[^?]+\?\s*\n\s*A\s*:[^\n]+\n\s*\n/gmi) || []).length
    } : {
      // Existing takeaway counting methods
      bulletPoints: (section.match(/(?:^|\n)\s*[-•*]\s+/gm) || []).length,
      numberedPoints: (section.match(/(?:^|\n)\s*\d+[\.)]\s+/gm) || []).length,
      newlines: (section.match(/\n\s*[-•*\d]\s+/g) || []).length,
      colonPoints: (section.match(/(?:^|\n)\s*[-•*\d]+[.:]\s+/gm) || []).length
    };

    console.log(`${type} counting methods results:`, methods);
    
    // For FAQs, prioritize exact format matches
    if (type === 'faq') {
      if (methods.exactFormatWithSpacing > 0) return { count: methods.exactFormatWithSpacing, methods };
      if (methods.exactFormat > 0) return { count: methods.exactFormat, methods };
      if (methods.numberedQA > 0) return { count: methods.numberedQA, methods };
      if (methods.questionAnswer > 0) return { count: methods.questionAnswer, methods };
      if (methods.numberedQuestions > 0) return { count: methods.numberedQuestions, methods };
      if (methods.bulletQuestions > 0) return { count: methods.bulletQuestions, methods };
      if (methods.headingQuestions > 0) return { count: methods.headingQuestions, methods };
      if (methods.simpleQuestions > 0) return { count: methods.simpleQuestions, methods };
    }
    
    const counts = Object.values(methods);
    const maxCount = Math.max(...counts);
    
    console.log(`Final ${type} count:`, maxCount);
    return { count: maxCount, methods };
  };

  if (includeFaqs) {
    console.log('\n=== Validating FAQs ===');
    const faqVariations = [
      sectionLabels.faq[language_code] || 'Frequently Asked Questions',
      'FAQ',
      'FAQs',
      'Q&A',
      'Questions',
      'Common Questions',
      'Popular Questions',
      'Questions and Answers',
      'Q & A',
      'Questions & Answers'
    ];

    console.log('Looking for FAQ section with variations:', faqVariations);
    const faqSection = extractSection(faqVariations, content);
    
    if (faqSection) {
      console.log('\nFAQ section found. Full section:', faqSection);
      const { count, methods } = countItems(faqSection, 'faq');
      console.log('FAQ validation:', {
        expected: numFaqs,
        found: count,
        methods,
        difference: Math.abs(count - numFaqs)
      });

      if (Math.abs(count - numFaqs) > 1) {
        console.error('FAQ count mismatch. Content preview:', content.substring(0, 1000));
        throw new Error(`FAQ count mismatch: Expected ${numFaqs}, found ${count}. Methods: ${JSON.stringify(methods)}`);
      }
    } else {
      console.error('FAQ section not found. Content preview:', content.substring(0, 1000));
      throw new Error(`FAQ section not found. Tried variations: ${faqVariations.join(', ')}`);
    }
  }

  if (includeTakeaways) {
    console.log('\n=== Validating Takeaways ===');
    const takeawayVariations = [
      sectionLabels.keyTakeaways[language_code] || 'Key Takeaways',
      'Summary',
      'Key Points',
      'Main Points',
      'Important Points',
      'Takeaways',
      'Main Takeaways'
    ];

    console.log('Looking for Takeaways section with variations:', takeawayVariations);
    const takeawaysSection = extractSection(takeawayVariations, content);
    
    if (takeawaysSection) {
      console.log('\nTakeaways section found. Full section:', takeawaysSection);
      const { count, methods } = countItems(takeawaysSection, 'takeaway');
      console.log('Takeaways validation:', {
        expected: numTakeaways,
        found: count,
        methods,
        difference: Math.abs(count - numTakeaways)
      });

      if (Math.abs(count - numTakeaways) > 1) {
        console.error('Takeaway count mismatch. Content preview:', content.substring(0, 1000));
        throw new Error(`Takeaway count mismatch: Expected ${numTakeaways}, found ${count}. Methods: ${JSON.stringify(methods)}`);
      }
    } else {
      console.error('Takeaways section not found. Content preview:', content.substring(0, 1000));
      throw new Error(`Takeaways section not found. Tried variations: ${takeawayVariations.join(', ')}`);
    }
  }

  console.log('=== Validation Completed Successfully ===');
  return modifiedContent;
}

// Enhanced content cleanup function
function cleanupFormatting(content, expectedTitle) {
  if (!content) return content;
  
  console.log('Starting content cleanup...');
  console.log('Original content first line:', content.split('\n')[0]);
  console.log('Expected title:', expectedTitle);
  
  // Normalize line endings and clean up whitespace
  let cleanContent = content
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .trim();

  // Ensure content starts with the expected title
  if (!cleanContent.startsWith(expectedTitle)) {
    cleanContent = expectedTitle + cleanContent.replace(/^[#\s]*\*\*[^*\n]*\*\*\n*/, '').trim() + '\n\n';
  }

  // Clean up section headers and make them bold
  cleanContent = cleanContent
    .replace(/\n#{2,3}(?!\s)/g, '\n## ')  // Fix headers without space after #
    .replace(/\n(#{2,3})\s+(?:\*\*)?([^#\n*]+?)(?:\*\*)?(?:\n|$)/g, (match, hashes, title) => {
      const titleText = title.trim();
      return `\n${hashes} **${titleText}**\n\n`;
    });

  // Ensure subsection headers are also bold
  cleanContent = cleanContent
    .replace(/\n###\s+(?:\*\*)?([^#\n*]+?)(?:\*\*)?(?:\n|$)/g, (match, title) => {
      const titleText = title.trim();
      return `\n### **${titleText}**\n\n`;
    });

  // Ensure there are exactly two newlines after headers
  cleanContent = cleanContent
    .replace(/^(#\s\*\*[^*\n]+\*\*)\n*/m, '$1\n\n')
    .replace(/\n(#{2,3}\s\*\*[^*\n]+\*\*)\n*/g, '\n$1\n\n');

  console.log('Final content first line:', cleanContent.split('\n')[0]);
  return cleanContent;
}

function validateFormatting(content, expectedTitle) {
  if (!content) {
    throw new Error('Content is empty');
  }

  console.log('Validating formatting...');
  console.log('First line:', content.split('\n')[0]);
  console.log('Expected title:', expectedTitle);

  // Check if content starts with the expected title
  if (!content.startsWith(expectedTitle)) {
    console.error('Title format validation failed');
    console.error('Expected:', expectedTitle);
    console.error('Found:', content.split('\n')[0]);
    throw new Error('Main title does not match the expected format');
  }

  // Check for unauthorized sections
  if (typeof includeFaqs !== 'undefined' && !includeFaqs && 
      content.match(/\n##\s+\*\*(FAQ|Frequently Asked Questions)\*\*/i)) {
    throw new Error('FAQ section found when not requested');
  }

  if (typeof includeTakeaways !== 'undefined' && !includeTakeaways && 
      content.match(/\n##\s+\*\*(Key Takeaways|Summary)\*\*/i)) {
    throw new Error('Key Takeaways section found when not requested');
  }

  return true;
}

export async function writeArticle(params) {
  try {
    console.log('\n=== Starting Article Generation ===');
    console.log('Raw params received:', {
      contentLength: params.contentLength,
      params: { ...params, content: undefined }
    });
    
    const { 
      keyword, 
      title, 
      keyPoints = [], 
      searchVolume, 
      competition, 
      competitionLevel, 
      seed_keyword,
      language_code,
      searchIntent,
      includeFaqs = false,
      includeTakeaways = false,
      numFaqs = 0,
      numTakeaways = 0,
      contentLength = 'long'
    } = params;

    // Get content length configuration
    const config = lengthConfig[contentLength];
    if (!config) {
      console.error('Invalid content length:', contentLength, 'Available configs:', Object.keys(lengthConfig));
      throw new Error(`Invalid content length: ${contentLength}`);
    }

    // Validate title
    if (!title || typeof title !== 'string' || title.trim().length === 0) {
      throw new Error('Title is required and must be a non-empty string');
    }

    // Format title for the prompt
    const formattedTitle = `# **${title.trim()}**\n\n`;
    console.log('Formatted title:', formattedTitle);

    // Format key points section
    const keyPointsSection = keyPoints && keyPoints.length > 0 
      ? `Key Points to Cover:\n${keyPoints.map((point, index) => `${index + 1}. ${point}`).join('\n')}`
      : '';

    const systemPrompt = `You are a professional content writer who specializes in writing in ${language_code.toUpperCase()}. 
${languageInstructions[language_code]?.instruction || "Write in the specified language"}
${languageInstructions[language_code]?.style || "Use appropriate style and grammar for the language"}

CRITICAL FORMATTING AND STYLE REQUIREMENTS:
1. The article MUST start with the EXACT title format I provide - DO NOT modify or change the title
2. The title format will be provided in the user message - use it exactly as given
3. Section headers must be formatted as: ## **Section Title**\n\n
4. Subsection headers must be formatted as: ### **Subsection Title**\n\n
5. All headers must be followed by exactly two newlines
6. Content must be properly formatted in Markdown
7. Write in a clear, professional style - avoid flowery or poetic language
8. Keep sentences concise and focused - avoid run-on sentences
9. Each paragraph should have a clear main point
10. Use proper punctuation and paragraph breaks

CRITICAL CONTENT STRUCTURE:
1. Introduction (500-600 words):
   - Clear thesis statement
   - Overview of main points
   - Context and background
   - NO philosophical rambling or metaphors

2. Key Takeaways (if requested):
   - Place immediately after introduction
   - Numbered list format
   - Each takeaway should be clear and actionable
   - Must match the requested number of takeaways

3. Main Body Sections (600-800 words each):
   - Each section must have a clear focus
   - Use concrete examples and data
   - Include relevant statistics when possible
   - Stay on topic - avoid tangents
   - Use proper subsections when needed

4. FAQ Section (if requested):
   - Place before conclusion
   - Clear Q&A format
   - Must match the requested number of FAQs
   - Each answer should be thorough and helpful

5. Conclusion (400-500 words):
   - Summarize key points
   - Provide actionable insights
   - Keep it focused and practical

STRICT CONTENT RULES:
1. Stay factual and objective
2. Avoid repetition
3. No philosophical or poetic tangents
4. Keep paragraphs focused and concise
5. Use clear topic sentences
6. Support claims with evidence
7. Maintain professional tone throughout
8. No run-on sentences or stream of consciousness writing

LENGTH REQUIREMENTS:
- Total content: Minimum ${config.targetWords} words
- Each main section: 600-800 words
- Introduction: 500-600 words
- Conclusion: 400-500 words
- Use detailed examples and evidence to meet length requirements, not filler text`;

    const userPrompt = `Here is the exact title to use at the start of the article:
${formattedTitle}

Write an article using this exact title and format. The article should be about:
Keyword: "${keyword}"
${keyPointsSection}

Additional Context:
- Search Volume: ${searchVolume || 'N/A'}
- Competition Level: ${competitionLevel || 'N/A'}
- Search Intent: ${searchIntent?.primary || 'informational'}

Requirements:
1. Use the title EXACTLY as provided above - do not modify it
2. Write in ${language_code.toUpperCase()} language
3. Cover the key points thoroughly in the main content
${includeTakeaways ? `4. Place the "${sectionLabels.keyTakeaways[language_code]}" section immediately after the introduction, with exactly ${numTakeaways} numbered takeaways` : ''}
${includeFaqs ? `5. Include the "${sectionLabels.faq[language_code]}" section before the conclusion, with exactly ${numFaqs} FAQs` : ''}`;

    const completion = await openai.chat.completions.create({
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPrompt }
      ],
      model: "gpt-4o-mini",
      temperature: 0.7,
      max_tokens: config.maxTokens,
      presence_penalty: 0.6,
      frequency_penalty: 0.3
    });

    let content = completion.choices[0].message.content.trim();
    
    // Ensure content starts with the correct title format
    if (!content.startsWith(formattedTitle)) {
      content = formattedTitle + content.replace(/^[#\s]*\*\*[^*\n]*\*\*\n*/, '').trim() + '\n\n';
    }

    // Clean up the formatting
    content = cleanupFormatting(content, formattedTitle);

    // Validate the formatting
    validateFormatting(content, formattedTitle);

    return content;

  } catch (error) {
    console.error('Error writing article:', error);
    throw error;
  }
}

// Update the validateSectionLength function to handle retries
async function validateSectionLength(section, index, openai, systemPrompt, maxRetries = 2) {
  const wordCount = countWords(section.content);
  const minWords = index === 0 ? 500 : 600; // 500 for intro, 600 for others
  
  if (wordCount < minWords) {
    if (maxRetries > 0) {
      console.log(`Section "${section.title}" is too short (${wordCount} words). Attempting to expand...`);
      
      // Create a prompt to expand the section
      const expansionPrompt = `Expand this section to at least ${minWords} words while maintaining quality and relevance:

Title: "${section.title}"
Current Content: "${section.content}"

Requirements:
1. Maintain the same topic and focus
2. Add more detailed examples
3. Include relevant statistics or research
4. Expand explanations
5. Add case studies or real-world applications
6. MUST be at least ${minWords} words
7. Keep the same style and tone`;

      try {
        const completion = await openai.chat.completions.create({
          messages: [
            { role: "system", content: systemPrompt },
            { role: "user", content: expansionPrompt }
          ],
          model: "gpt-4o-mini",
          temperature: 0.7,
          max_tokens: 2000,
          presence_penalty: 0.6,
          frequency_penalty: 0.3
        });

        const expandedContent = completion.choices[0].message.content;
        const expandedWordCount = countWords(expandedContent);

        if (expandedWordCount >= minWords) {
          section.content = expandedContent;
          return;
        }
        
        return validateSectionLength(section, index, openai, systemPrompt, maxRetries - 1);
      } catch (error) {
        console.error('Error expanding section:', error);
        throw new Error(`Section "${section.title}" is too short (${wordCount} words). Minimum required: ${minWords} words`);
      }
    }
    
    throw new Error(`Section "${section.title}" is too short (${wordCount} words). Minimum required: ${minWords} words`);
  }
  
  if (section.subsections) {
    section.subsections.forEach((subsection, subIndex) => {
      const subWordCount = countWords(subsection.content);
      if (subWordCount < 300) {
        throw new Error(`Subsection "${subsection.title}" in section "${section.title}" is too short (${subWordCount} words). Minimum required: 300 words`);
      }
    });
  }
}
